import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Docmnt } from '/imports/types';
import { filterBoolean, mapById, randomString } from '/imports/utils';
import { DataTable, DataTableProps } from '/imports/ui/DataTable';
import { Catalog } from '/imports/ui/Catalog';
import { Input } from '/imports/ui/Input';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { panelSurface } from '/imports/ui/Panel';

export interface DataCatalogProps<T extends Docmnt = any> extends DataTableProps<T> {
  tools?: React.ReactNode;
  count?: number;
  loading?: boolean;
  softLoading?: boolean;
  element?: (d: T) => React.ReactNode;
  row?: (d: T) => React.ReactNode;
  rowClass?: (d: T) => string;
  searchFilter?(d: T, search?: string): boolean;
  onSearch?(search?: string): void;
  onSearchEnter?(search?: string): void;
  onDelete?(_ids: string[]): void;
  className?: string;
}

export const DataCatalog = <T extends Docmnt,>(props: DataCatalogProps<T>) => {
  const { data, references, columns, filters, pageSize, onScrollBottom, className, onDelete,
    onSelectedIds, updateMethod, deleteMethod, tools, searchFilter, count, loading, softLoading, element, row, rowClass, allIds } = props;
  const [dataMap, setDataMap] = useState(mapById(data));
  const [search, setSearch] = useState('');
  const [searchId,] = useState(randomString());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [renderKey, setRenderKey] = useState(randomString());
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const _count = count === undefined ? data.length : count;
  const _searchFilter = searchFilter || ((d, s) => {
    const _search = s?.toLowerCase().trim();
    if (!_search) return true;
    for (const c of filterBoolean(columns || [])) {
      const v = `${(d as any)[c.field] || ''}`.toLowerCase();
      if (v.includes(_search)) return true;
    }
    return false;
  });
  const srchFilter = (d: T) => _searchFilter(d, search);
  useEffect(() => {
    props.onSearch?.(search);
  }, [search]);
  useEffect(() => {
    setDataMap(mapById(data));
  }, [data]);
  const _onSelectedIds = (selectedIds: string[]) => {
    setSelectedIds(selectedIds);
    onSelectedIds?.(selectedIds);
  };
  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    let deleted = 0;
    for (const _id of selectedIds) {
      try {
        await deleteMethod?.call(_id);
        deleted += 1;
        dataMap[_id].active = false;
      }
      catch (error) {
        console.log(error);
      }
    }
    toast.success(`${strings.Deleted()} ${deleted}`);
    onDelete?.(selectedIds);
    setSelectedIds([]);
    setRenderKey(randomString());
  };
  const activateSelected = async () => {
    if (!selectedIds.length) return;
    let updated = 0;
    for (const _id of selectedIds) {
      try {
        await updateMethod?.call({ _id, active: true } as unknown as Partial<T>);
        updated += 1;
        dataMap[_id].active = true;
      }
      catch (error) {
        console.log(error);
      }
    }
    toast.success(`${strings.Updated()} ${updated}`);
    setSelectedIds([]);
    setRenderKey(randomString());
  };
  return (
    <Catalog className={className}>
      <div className="catalog-tools">
        <Input id={searchId} value={search} onChange={(search) => setSearch(search || '')} icon="search" className="search" placeholder={strings.Search()} onEnter={() => props.onSearchEnter?.(search)} />
        {deleteMethod && selectedIds.length && <Button onClick={() => deleteSelected()}>{strings.DeleteSelected()} ({selectedIds.length})</Button> || null}
        {updateMethod && selectedIds.length && <Button onClick={() => activateSelected()}>{strings.ActivateSelected()} ({selectedIds.length})</Button> || null}
        <div className={`custom-tools${mobileToolsOpen ? ' open' : ''}`}>
          {tools}
        </div>
        <div className={`custom-tools-mobile-opener${mobileToolsOpen ? ' open' : ''}`} onClick={() => setMobileToolsOpen(!mobileToolsOpen)}>
          <Icon icon="tune" />
        </div>
      </div>
      {/*
        The table and its result count share one panel, which is the console's
        most recognisable silhouette. This div MUST keep `flex min-h-0 flex-1
        flex-col`: Catalog is a flex column, DataTable's inner `.catalog-table`
        is `flex-1 min-h-0 overflow-auto`, and that chain is what makes the TABLE
        scroll rather than the page. Break it and all 18 list screens silently
        start scrolling the whole body instead.
      */}
      <div className={`catalog-panel flex min-h-0 flex-1 flex-col overflow-hidden ${panelSurface}`}>
        <DataTable
          key={renderKey}
          data={data}
          allIds={allIds}
          references={references}
          columns={columns}
          pageSize={pageSize}
          filters={[...(filters || []), srchFilter]}
          onScrollBottom={onScrollBottom}
          updateMethod={updateMethod}
          deleteMethod={deleteMethod}
          onSelectedIds={_onSelectedIds}
          sort={props.sort}
          onClickColumn={props.onClickColumn}
          loading={loading}
          softLoading={softLoading}
          element={element}
          row={row}
          rowClass={rowClass}
        />
        {
          loading ? null : <div className="catalog-stats">{_count} {_count === 1 ? strings.Result() : strings.Results()}</div>
        }
      </div>
    </Catalog>
  );
};